import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import useSocket from '../hooks/useSocket';
import useTurnNotifications from '../hooks/useTurnNotifications';
import {
  getGame,
  respondWaitlistOffer,
  proposeCollapseInactiveRedeal,
  voteCollapseInactiveRedeal,
  bumpPlayerToWaitlist,
  clearNpcs,
  leaveGame,
  getChatMessagesByTarget,
  deleteChatMessage,
} from '../api/gameApi';
import SwapPhase from '../components/SwapPhase';
import GameTable from '../components/GameTable';
import ThinHeader from '../components/ThinHeader';
import UniversalChatDock from '../components/UniversalChatDock';
import useRoomVoiceChat from '../hooks/useRoomVoiceChat';
import VoiceRemoteAudio from '../components/VoiceRemoteAudio';
import {
  CHAT_SCOPE,
  getUserId,
  buildRoomLoungeTarget,
  buildDmTarget,
  buildDmInboxTarget,
  toNormalizedMessage,
  messageMatchesTarget,
  buildOutgoingMessage,
  getPlaceholderForTarget,
  dedupeAndSortNormalizedMessages,
  computeUnreadByTarget,
} from '../utils/universalChat';

const formatRoomStatus = (status) => {
  if (status === 'lobby' || status === 'finished') return 'Waiting Room';
  if (status === 'swap') return 'Game Setup (Swap)';
  if (status === 'playing') return 'Game In Play';
  return status || 'Unknown';
};

const toDmVoicePipeId = (currentUserId, peerUserId) => {
  const ids = [`${currentUserId || ''}`.trim(), `${peerUserId || ''}`.trim()].filter(Boolean).sort();
  if (ids.length !== 2) return 'room';
  return `dm:${ids[0]}:${ids[1]}`;
};

const toVoicePipeIdFromTarget = (target, currentUserId) => {
  if (!target) return 'room';
  if (target.type === CHAT_SCOPE.DM && target.recipientId) {
    return toDmVoicePipeId(currentUserId, target.recipientId);
  }
  return 'room';
};

const toShortPipeCode = (value) => {
  const raw = `${value || ''}`.trim();
  if (!raw) return '';
  const clean = raw.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
  return clean.slice(-6) || clean;
};

const formatJoinWindowReason = (reason) => {
  if (reason === 'lobby-seat-change') return 'A new player joined. Waitlist response window reset';
  if (reason === 'round-finished') return 'Round ended. Confirming waitlist entries for next round';
  return 'Waitlist response window active';
};

const GamePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    socket,
    gameState,
    error,
    clearError,
    swapCards,
    markReady,
    playCard,
    pickupPile,
    openingPass,
    sendLoungeChat,
    setGameName,
    playAgainReady,
    playResult,
    gameOver,
    inactivityBootEvent,
    inactivityCheckPrompt,
    waitlistJoinOffer,
    respondInactivityCheck,
    voiceEvent,
    joinVoiceRoom,
    leaveVoiceRoom,
    sendVoiceOffer,
    sendVoiceAnswer,
    sendVoiceIceCandidate,
    sendVoiceDecline,
  } = useSocket(id);
  const [showSocketModal, setShowSocketModal] = useState(false);
  const [showHeaderLeaderboard, setShowHeaderLeaderboard] = useState(false);
  const [pendingJoinRequests, setPendingJoinRequests] = useState([]);
  const [queuedPlayers, setQueuedPlayers] = useState([]);
  const [waitingError, setWaitingError] = useState('');
  const [breakError, setBreakError] = useState('');
  const [collapsingInactive, setCollapsingInactive] = useState(false);
  const [bumpingUserId, setBumpingUserId] = useState('');
  const [chatText, setChatText] = useState('');
  const [selectedChatTargetId, setSelectedChatTargetId] = useState(() => `room-game:${id}`);
  const [chatCollapsed, setChatCollapsed] = useState(true);
  const [selectedTargetHistoryMessages, setSelectedTargetHistoryMessages] = useState([]);
  const [lastSeenAtByTargetId, setLastSeenAtByTargetId] = useState({});
  const [activeWaitlistOffer, setActiveWaitlistOffer] = useState(null);
  const [offerSecondsRemaining, setOfferSecondsRemaining] = useState(0);
  const [activeInactivityPrompt, setActiveInactivityPrompt] = useState(null);
  const [inactivityPromptSecondsRemaining, setInactivityPromptSecondsRemaining] = useState(0);
  const [respondingToInactivityPrompt, setRespondingToInactivityPrompt] = useState(false);
  const [roundWindowSecondsRemaining, setRoundWindowSecondsRemaining] = useState(0);
  const [respondingToOffer, setRespondingToOffer] = useState(false);
  const [quittingRoom, setQuittingRoom] = useState(false);
  const canModerateChat = ['admin', 'moderator', 'stickler'].includes(user?.role);
  const lastWaitlistOfferSignatureRef = useRef('');
  const dismissedWaitlistOfferIdsRef = useRef(new Set());
  const waitlistDeclineBaselineRef = useRef(null);
  const [gameNameInput, setGameNameInput] = useState('');
  const [roomSettingsOpen, setRoomSettingsOpen] = useState(false);
  const wasMyTurnRef = useRef(false);
  const {
    sendTurnNotification,
  } = useTurnNotifications();

  const getCurrentSeatedHumanIds = useCallback((state) => {
    const players = Array.isArray(state?.players) ? state.players : [];
    return new Set(
      players
        .filter((player) => player?.userId && !player?.isBot)
        .map((player) => `${player.userId}`)
    );
  }, []);

  const canShowNewWaitlistOffer = useCallback((state) => {
    const baseline = waitlistDeclineBaselineRef.current;
    if (!baseline) return true;

    const currentSeated = getCurrentSeatedHumanIds(state);
    const hasNewSeatedJoin = Array.from(currentSeated).some((userId) => !baseline.has(userId));
    if (hasNewSeatedJoin) {
      waitlistDeclineBaselineRef.current = null;
      return true;
    }
    return false;
  }, [getCurrentSeatedHumanIds]);

  const {
    isJoined: voiceJoined,
    isMuted: voiceMuted,
    voiceError,
    activeTarget: activeVoiceTarget,
    activePipeId,
    remotePeers,
    join: joinVoice,
    joinTarget: joinVoiceTarget,
    leave: leaveVoice,
    toggleMute: toggleVoiceMute,
    callPeer,
    incomingCall,
    acceptIncomingCall,
    declineIncomingCall,
  } = useRoomVoiceChat({
    voiceTarget: 'game',
    localUserId: user?.id,
    localDisplayName: user?.displayName || user?.username || 'Player',
    voiceEvent,
    joinVoiceRoom,
    leaveVoiceRoom,
    sendVoiceOffer,
    sendVoiceAnswer,
    sendVoiceIceCandidate,
    sendVoiceDecline,
    socket,
  });

  useEffect(() => {
    if (error) setShowSocketModal(true);
  }, [error]);

  useEffect(() => {
    if (!id) return undefined;

    let mounted = true;
    const pollWaitingRoom = async () => {
      try {
        const { data } = await getGame(id);
        if (!mounted) return;
        setPendingJoinRequests(data.pendingJoinRequests || []);
        setQueuedPlayers(data.queuedPlayers || []);

        // Fallback path: if a socket push is missed, hydrate offer/prompt state from polling.
        const polledWaitlistOffer = data?.viewerWaitlistOffer || null;
        const offerId = `${polledWaitlistOffer?.offerId || ''}`;
        if (
          offerId
          && !data?.viewerIsSeated
          && !dismissedWaitlistOfferIdsRef.current.has(offerId)
          && canShowNewWaitlistOffer(data)
        ) {
          setActiveWaitlistOffer(polledWaitlistOffer);
          setOfferSecondsRemaining(Number(polledWaitlistOffer.secondsRemaining || 0));
        } else {
          setActiveWaitlistOffer(null);
          setOfferSecondsRemaining(0);
        }

        const polledInactivityPrompt = data?.viewerInactivityPrompt || null;
        if (polledInactivityPrompt?.promptId) {
          setActiveInactivityPrompt(polledInactivityPrompt);
          setInactivityPromptSecondsRemaining(Number(polledInactivityPrompt.secondsRemaining || 0));
        } else {
          setActiveInactivityPrompt(null);
          setInactivityPromptSecondsRemaining(0);
        }
      } catch {
        // Ignore polling failures; socket state remains primary.
      }
    };

    pollWaitingRoom();
    const timer = setInterval(pollWaitingRoom, 4000);
    return () => {
      mounted = false;
      clearInterval(timer);
    };
  }, [canShowNewWaitlistOffer, id]);

  useEffect(() => {
    if (!gameState || !user?.id) return;

    const players = gameState.players || [];
    const currentTurnUserId = players[gameState.currentTurnIndex]?.userId?.toString();
    const isMyTurnNow = gameState.status === 'playing' && currentTurnUserId === String(user.id);

    if (!wasMyTurnRef.current && isMyTurnNow) {
      sendTurnNotification({
        title: 'Necronomicon: Your turn',
        body: `It is your turn in ${gameState.gameName || `Room ${gameState.roomCode}`}.`,
        tag: `turn-game-${id}`,
        targetPath: `/game/${id}/play`,
      });
    }

    wasMyTurnRef.current = isMyTurnNow;
  }, [gameState, id, sendTurnNotification, user?.id]);

  useEffect(() => {
    const resolvedName = gameState?.gameName || (gameState?.roomCode ? `Room ${gameState.roomCode}` : '');
    if (!resolvedName) return;
    setGameNameInput(resolvedName);
  }, [gameState?.gameName, gameState?.roomCode]);

  useEffect(() => {
    const nextTargetId = `room-game:${id}`;
    setSelectedChatTargetId(nextTargetId);
    setLastSeenAtByTargetId((prev) => ({
      ...prev,
      [nextTargetId]: new Date().toISOString(),
    }));
  }, [id]);

  useEffect(() => {
    const targetId = `${selectedChatTargetId || ''}`;
    const parts = targetId.split(':');
    const kind = parts[0] || '';
    const value = parts.slice(1).join(':');

    const target = {
      type: kind === 'room-lounge'
        ? CHAT_SCOPE.ROOM_GAME
        : kind === 'room-game'
          ? CHAT_SCOPE.ROOM_GAME
          : kind === 'dm'
            ? CHAT_SCOPE.DM
            : kind === 'home-lounge'
              ? CHAT_SCOPE.LOUNGE
              : null,
      roomId: (kind === 'room-lounge' || kind === 'room-game') ? value : null,
      recipientId: kind === 'dm' ? value : null,
    };
    if (!target.type) return;

    let cancelled = false;
    const loadTargetHistory = async () => {
      try {
        const { data } = await getChatMessagesByTarget({
          scope: target.type,
          roomId: target.roomId || undefined,
          recipientId: target.recipientId || undefined,
          limit: 160,
        });
        if (cancelled) return;
        setSelectedTargetHistoryMessages(data?.messages || []);
      } catch {
        if (cancelled) return;
        setSelectedTargetHistoryMessages([]);
      }
    };

    loadTargetHistory();
    return () => {
      cancelled = true;
    };
  }, [selectedChatTargetId]);

  useEffect(() => {
    if (!inactivityBootEvent) return;
    setWaitingError(inactivityBootEvent.message || 'Tournament room was inactive and players were moved to waiting room.');
    navigate(`/game/${id}?view=waiting`);
  }, [id, inactivityBootEvent, navigate]);

  useEffect(() => {
    const offer = gameState?.viewerWaitlistOffer || null;
    const offerId = `${offer?.offerId || ''}`;
    if (
      !offerId
      || gameState?.viewerIsSeated
      || dismissedWaitlistOfferIdsRef.current.has(offerId)
      || !canShowNewWaitlistOffer(gameState)
    ) {
      setActiveWaitlistOffer(null);
      setOfferSecondsRemaining(0);
      return;
    }
    setActiveWaitlistOffer(offer);
    setOfferSecondsRemaining(Number(offer.secondsRemaining || 0));
  }, [canShowNewWaitlistOffer, gameState]);

  useEffect(() => {
    const prompt = gameState?.viewerInactivityPrompt || null;
    if (!prompt?.promptId) {
      setActiveInactivityPrompt(null);
      setInactivityPromptSecondsRemaining(0);
      return;
    }
    setActiveInactivityPrompt(prompt);
    setInactivityPromptSecondsRemaining(Number(prompt.secondsRemaining || 0));
  }, [gameState?.viewerInactivityPrompt]);

  useEffect(() => {
    if (!inactivityCheckPrompt?.promptId) return;
    const targetedUserIds = Array.isArray(inactivityCheckPrompt.targetedUserIds)
      ? inactivityCheckPrompt.targetedUserIds.map((uid) => `${uid}`)
      : [];
    if (targetedUserIds.length > 0 && !targetedUserIds.includes(String(user?.id || ''))) return;

    const expiresAtMs = inactivityCheckPrompt.expiresAt ? new Date(inactivityCheckPrompt.expiresAt).getTime() : NaN;
    const seconds = Number.isFinite(expiresAtMs)
      ? Math.max(0, Math.ceil((expiresAtMs - Date.now()) / 1000))
      : Math.max(0, Number(inactivityCheckPrompt.secondsRemaining || 0));
    setActiveInactivityPrompt(inactivityCheckPrompt);
    setInactivityPromptSecondsRemaining(seconds);
  }, [inactivityCheckPrompt, user?.id]);

  useEffect(() => {
    if (!waitlistJoinOffer?.offerId) return;
    const eventUserId = waitlistJoinOffer.userId?.toString?.() || '';
    if (eventUserId && eventUserId !== String(user?.id || '')) return;
    if (gameState?.viewerIsSeated) return;
    if (!canShowNewWaitlistOffer(gameState)) return;

    const currentOfferId = `${waitlistJoinOffer.offerId || ''}`;
    if (dismissedWaitlistOfferIdsRef.current.has(currentOfferId)) return;
    const offerSignature = `${waitlistJoinOffer.offerId}:${waitlistJoinOffer.expiresAt || ''}`;
    if (lastWaitlistOfferSignatureRef.current === offerSignature) return;
    lastWaitlistOfferSignatureRef.current = offerSignature;

    const expiresAtMs = new Date(waitlistJoinOffer.expiresAt).getTime();
    const remaining = Number.isFinite(expiresAtMs)
      ? Math.max(0, Math.ceil((expiresAtMs - Date.now()) / 1000))
      : 60;
    setActiveWaitlistOffer(waitlistJoinOffer);
    setOfferSecondsRemaining(remaining);
  }, [canShowNewWaitlistOffer, gameState, user?.id, waitlistJoinOffer]);

  useEffect(() => {
    if (!activeWaitlistOffer?.offerId) return undefined;
    const timer = setInterval(() => {
      setOfferSecondsRemaining((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [activeWaitlistOffer?.offerId]);

  useEffect(() => {
    if (!activeInactivityPrompt?.promptId) return undefined;
    const timer = setInterval(() => {
      setInactivityPromptSecondsRemaining((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [activeInactivityPrompt?.promptId]);

  useEffect(() => {
    const roundWindow = gameState?.roundJoinOfferWindow;
    if (!roundWindow?.active) {
      setRoundWindowSecondsRemaining(0);
      return;
    }
    setRoundWindowSecondsRemaining(Number(roundWindow.secondsRemaining || 0));
  }, [gameState?.roundJoinOfferWindow]);

  useEffect(() => {
    if (!gameState?.roundJoinOfferWindow?.active) return undefined;
    if (roundWindowSecondsRemaining <= 0) return undefined;
    const timer = setInterval(() => {
      setRoundWindowSecondsRemaining((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [gameState?.roundJoinOfferWindow?.active, roundWindowSecondsRemaining]);

  const handleWaitlistOfferResponse = useCallback(async (accept) => {
    if (!activeWaitlistOffer?.offerId || respondingToOffer) return;
    try {
      setRespondingToOffer(true);
      if (!accept) {
        dismissedWaitlistOfferIdsRef.current.add(`${activeWaitlistOffer.offerId}`);
        waitlistDeclineBaselineRef.current = getCurrentSeatedHumanIds(gameState);
      } else {
        waitlistDeclineBaselineRef.current = null;
      }
      const response = await respondWaitlistOffer(id, activeWaitlistOffer.offerId, accept);
      setActiveWaitlistOffer(null);
      setOfferSecondsRemaining(0);

      const serverGame = response?.data?.game;
      if (serverGame) {
        setPendingJoinRequests(serverGame.pendingJoinRequests || []);
        setQueuedPlayers(serverGame.queuedPlayers || []);
        if (serverGame.status === 'lobby') {
          navigate(`/game/${id}`, { replace: true });
          return;
        }
      }

      const { data } = await getGame(id);
      setPendingJoinRequests(data.pendingJoinRequests || []);
      setQueuedPlayers(data.queuedPlayers || []);
      if (data?.status === 'lobby') {
        navigate(`/game/${id}`, { replace: true });
      }
    } catch (err) {
      setWaitingError(err.response?.data?.error || 'Could not process waitlist offer');
      if (!accept) {
        setActiveWaitlistOffer(null);
        setOfferSecondsRemaining(0);
      }
    } finally {
      setRespondingToOffer(false);
    }
  }, [activeWaitlistOffer?.offerId, gameState, getCurrentSeatedHumanIds, id, navigate, respondingToOffer]);

  const handleInactivityPromptResponse = useCallback(async (stillPlaying) => {
    if (!activeInactivityPrompt?.promptId || respondingToInactivityPrompt) return;
    try {
      setRespondingToInactivityPrompt(true);
      await respondInactivityCheck(activeInactivityPrompt.promptId, !!stillPlaying);
      setActiveInactivityPrompt(null);
      setInactivityPromptSecondsRemaining(0);
      const { data } = await getGame(id);
      setPendingJoinRequests(data.pendingJoinRequests || []);
      setQueuedPlayers(data.queuedPlayers || []);
    } catch (err) {
      setWaitingError(err?.message || 'Could not process inactivity confirmation');
      if (!stillPlaying) {
        setActiveInactivityPrompt(null);
        setInactivityPromptSecondsRemaining(0);
      }
    } finally {
      setRespondingToInactivityPrompt(false);
    }
  }, [activeInactivityPrompt?.promptId, id, respondInactivityCheck, respondingToInactivityPrompt]);

  useEffect(() => {
    if (!activeWaitlistOffer?.offerId) return;
    if (offerSecondsRemaining > 0) return;
    handleWaitlistOfferResponse(false);
  }, [activeWaitlistOffer?.offerId, offerSecondsRemaining, handleWaitlistOfferResponse]);

  useEffect(() => {
    if (!activeInactivityPrompt?.promptId) return;
    if (inactivityPromptSecondsRemaining > 0) return;
    handleInactivityPromptResponse(false);
  }, [activeInactivityPrompt?.promptId, handleInactivityPromptResponse, inactivityPromptSecondsRemaining]);

  useEffect(() => {
    if (!roomSettingsOpen) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setRoomSettingsOpen(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [roomSettingsOpen]);

  useEffect(() => {
    if (!gameState) return;
    if (gameState.status === 'lobby') {
      navigate(`/game/${id}`, { replace: true });
    }
  }, [gameState, id, navigate]);

  if (!gameState) return <div className="game-page"><p>Connecting...</p></div>;

  const myUserId = user?.id;
  const myState = gameState.players.find((p) => p.userId?.toString() === myUserId);
  const readyCount = gameState.players.filter((p) => p.swapReady).length;
  const totalCount = gameState.players.length;
  const roundFinishedOrder = gameOver?.finishedOrder ?? gameState.finishedOrder ?? [];
  const loungeChatMessages = gameState.loungeChatMessages || [];
  const gameChatMessages = gameState.gameChatMessages || [];
  const chatParticipants = (gameState.chatParticipants || []).filter((entry) => `${entry.userId}` !== `${myUserId || ''}`);
  const chatTargets = [
    buildRoomLoungeTarget({ roomId: id, roomName: gameState.gameName, roomCode: gameState.roomCode }),
    buildDmInboxTarget(),
    ...chatParticipants.map((entry) => buildDmTarget(entry)),
  ];
  const selectedChatTarget = chatTargets.find((entry) => entry.id === selectedChatTargetId)
    || chatTargets.find((entry) => entry.id === `room-game:${id}`)
    || chatTargets[0];

  const normalizedMessages = dedupeAndSortNormalizedMessages([
    ...loungeChatMessages.map((msg) => toNormalizedMessage({ message: msg, fallbackScope: CHAT_SCOPE.ROOM_GAME, roomId: id })),
    ...gameChatMessages.map((msg) => toNormalizedMessage({ message: msg, fallbackScope: CHAT_SCOPE.ROOM_GAME, roomId: id })),
  ]).filter(Boolean);

  const selectedTargetHistoryNormalized = dedupeAndSortNormalizedMessages(
    (selectedTargetHistoryMessages || []).map((msg) => toNormalizedMessage({
      message: msg,
      fallbackScope: selectedChatTarget?.type || CHAT_SCOPE.ROOM_GAME,
      roomId: selectedChatTarget?.roomId || id,
    }))
  ).filter(Boolean);

  const visibleMessages = dedupeAndSortNormalizedMessages([
    ...normalizedMessages,
    ...selectedTargetHistoryNormalized,
  ]).filter((msg) => messageMatchesTarget(msg, selectedChatTarget, getUserId(user)));

  const unread = computeUnreadByTarget({
    targets: chatTargets,
    messages: dedupeAndSortNormalizedMessages([
      ...normalizedMessages,
      ...selectedTargetHistoryNormalized,
    ]),
    currentUserId: getUserId(user),
    lastSeenAtByTargetId,
  });
  const unreadByTargetId = {
    ...unread.unreadByTargetId,
    [selectedChatTarget?.id || '']: 0,
  };
  const totalUnread = Object.values(unreadByTargetId).reduce((sum, value) => sum + Number(value || 0), 0);
  const unreadNotices = unread.notices.filter((entry) => entry.targetId !== selectedChatTarget?.id);

  const selectedTargetParticipants = selectedChatTarget?.type === CHAT_SCOPE.DM
    ? chatParticipants.filter((entry) => `${entry.userId}` === `${selectedChatTarget.recipientId || ''}`)
    : chatParticipants;

  const currentUserId = getUserId(user);
  const selectedVoicePipeId = toVoicePipeIdFromTarget(selectedChatTarget, currentUserId);
  const roomVoiceLabel = chatTargets.find((entry) => entry.type === CHAT_SCOPE.ROOM_GAME)?.label || 'Room Pipe';
  const pipeLabelById = {
    room: roomVoiceLabel,
  };
  chatTargets
    .filter((entry) => entry.type === CHAT_SCOPE.DM && entry.recipientId)
    .forEach((entry) => {
      const pipeId = toDmVoicePipeId(currentUserId, entry.recipientId);
      pipeLabelById[pipeId] = `Private Pipe ${toShortPipeCode(pipeId)}`;
    });
  const selectedVoicePipeLabel = pipeLabelById[selectedVoicePipeId] || 'Room Pipe';
  const activeVoicePipeCode = toShortPipeCode(activePipeId);
  const activeVoicePipeLabelBase = pipeLabelById[activeVoiceTarget]
    || (activeVoiceTarget?.startsWith('dm:') ? `Private Pipe ${toShortPipeCode(activeVoiceTarget)}` : roomVoiceLabel);
  const activeVoicePipeLabel = activeVoicePipeCode
    ? `${activeVoicePipeLabelBase} [${activeVoicePipeCode}]`
    : activeVoicePipeLabelBase;

  const markTargetSeen = (targetId) => {
    if (!targetId) return;
    setLastSeenAtByTargetId((prev) => ({
      ...prev,
      [targetId]: new Date().toISOString(),
    }));
  };

  const handleSelectTarget = (targetId) => {
    setSelectedChatTargetId(targetId);
    markTargetSeen(targetId);
  };

  const removeMessageById = (entry, messageId) => `${entry?.messageId || entry?._id || entry?.id || ''}` !== `${messageId || ''}`;

  const handleDeleteChatMessage = async (msg) => {
    const messageId = `${msg?.id || ''}`;
    if (!messageId) return;
    try {
      await deleteChatMessage(messageId, {
        scope: msg?.scope || selectedChatTarget?.type || '',
        roomId: id,
      });
      setSelectedTargetHistoryMessages((prev) => prev.filter((entry) => removeMessageById(entry, messageId)));
    } catch (err) {
      setBreakError(err.response?.data?.error || 'Could not delete chat message');
    }
  };

  const gameName = gameState.gameName || `Room ${gameState.roomCode}`;
  const sessionStats = [...(gameOver?.sessionStats ?? gameState.sessionStats ?? [])]
    .sort((a, b) => (b.totalPoints || 0) - (a.totalPoints || 0));
  const rematchReadyHumans = gameState.players.filter((p) => !p.isBot && p.rematchReady).length;
  const rematchTotalHumans = gameState.players.filter((p) => !p.isBot).length;
  const pendingSwapPlayers = gameState.players
    .filter((p) => !p.isBot && !p.swapReady)
    .map((p) => p.displayName);
  const pendingRematchPlayers = gameState.players
    .filter((p) => !p.isBot && !p.rematchReady)
    .map((p) => p.displayName);
  const myRematchReady = !!myState?.rematchReady;
  const userLabel = user?.displayName || user?.username || user?.email || 'Player';
  const viewerIsSeated = !!gameState?.viewerIsSeated;
  const canManageWaitingRoom = !!gameState?.canManageWaitingRoom;
  const canCollapseInactive = !!viewerIsSeated || canManageWaitingRoom;
  const canBumpPlayers = !!gameState?.canBumpPlayers;
  const canManageNpcs = !!gameState?.canManageNpcs;
  const botCount = (gameState.players || []).filter((p) => p.isBot).length;
  const isStickler = user?.role === 'stickler';
  const myFinishEntry = roundFinishedOrder.find((entry) => (entry.userId ?? '').toString() === (myUserId ?? '').toString());
  const roomStatusLabel = formatRoomStatus(gameState.status);
  const seatedHumans = (gameState.players || []).filter((p) => !p.isBot);
  const seatedHumanCount = seatedHumans.length;
  const activeSeatedHumanCount = seatedHumans.filter((p) => p.isActiveInRoom).length;
  const waitingCount = pendingJoinRequests.length + queuedPlayers.length;
  const waitingQueue = [...pendingJoinRequests, ...queuedPlayers].filter((entry, index, arr) => {
    const idKey = entry?.userId?.toString?.() || entry?.displayName || `${index}`;
    return arr.findIndex((candidate) => (candidate?.userId?.toString?.() || candidate?.displayName || '') === idKey) === index;
  });
  const showRoomSettingsEntry = ['swap', 'playing', 'finished'].includes(gameState.status);

  const handleSendChat = (e) => {
    e.preventDefault();
    const text = chatText.trim();
    if (!text) return;
    const target = chatTargets.find((entry) => entry.id === selectedChatTargetId);
    if (!target) return;
    if (target.type === CHAT_SCOPE.DM_INBOX) return;
    const outgoing = buildOutgoingMessage(target, user, text);

    if (target.type === CHAT_SCOPE.DM) {
      sendLoungeChat(outgoing.body, outgoing.recipientId || undefined);
    } else {
      sendLoungeChat(outgoing.body);
    }
    setChatText('');
  };

  const handleSaveGameName = (e) => {
    e.preventDefault();
    const trimmed = gameNameInput.trim();
    if (!trimmed) return;
    setGameName(trimmed);
  };

  const handleTakeBreakToLounge = async () => {
    try {
      setBreakError('');
      navigate(`/game/${id}?view=waiting`);
    } catch (err) {
      setBreakError(err.response?.data?.error || 'Could not open waiting room');
    }
  };

  const handleQuitAfterRound = async () => {
    try {
      setBreakError('');
      setQuittingRoom(true);
      await leaveGame(id);
      navigate('/');
    } catch (err) {
      setBreakError(err.response?.data?.error || 'Could not leave room');
    } finally {
      setQuittingRoom(false);
    }
  };

  const handleCollapseInactiveFromSwap = async () => {
    try {
      setCollapsingInactive(true);
      setBreakError('');
      await proposeCollapseInactiveRedeal(id);
    } catch (err) {
      setBreakError(err.response?.data?.error || 'Could not start collapse proposal');
    } finally {
      setCollapsingInactive(false);
    }
  };

  const handleVoteCollapseFromSwap = async (approve = true) => {
    try {
      setCollapsingInactive(true);
      setBreakError('');
      await voteCollapseInactiveRedeal(id, approve);
    } catch (err) {
      setBreakError(err.response?.data?.error || 'Could not record collapse vote');
    } finally {
      setCollapsingInactive(false);
    }
  };

  const handleBumpFromGame = async (targetUserId) => {
    try {
      setWaitingError('');
      setBumpingUserId(targetUserId);
      await bumpPlayerToWaitlist(id, targetUserId);
      const { data } = await getGame(id);
      setPendingJoinRequests(data.pendingJoinRequests || []);
      setQueuedPlayers(data.queuedPlayers || []);
    } catch (err) {
      setWaitingError(err.response?.data?.error || 'Could not bump player');
    } finally {
      setBumpingUserId('');
    }
  };

  const handleEmergencyStopBotsFromGame = async () => {
    try {
      setWaitingError('');
      setBreakError('');
      setCollapsingInactive(true);
      await clearNpcs(id);
    } catch (err) {
      setWaitingError(err.response?.data?.error || 'Could not stop bots');
    } finally {
      setCollapsingInactive(false);
    }
  };

  return (
    <div className="game-page">
      <ThinHeader
        homeTo="/"
        onToggleLeaderboard={() => setShowHeaderLeaderboard((prev) => !prev)}
        leaderboardOpen={showHeaderLeaderboard}
        userLabel={userLabel}
        showAdminLink={user?.role === 'admin'}
      />

      {showHeaderLeaderboard && (
        <div className="header-leaderboard-panel">
          <h3>Session Leaderboard</h3>
          <ul>
            {sessionStats.map((entry, idx) => (
              <li key={`${entry.userId ?? 'bot'}-${entry.displayName}`}>
                <span className="leader-rank">#{idx + 1}</span>
                <span className="leader-name">{entry.displayName}</span>
                <span className="leader-metrics">
                  {entry.totalPoints ?? 0} pts • {entry.roundsPlayed ?? 0} rounds • {entry.roundsWon ?? 0} wins • {entry.courtesyShuffles ?? 0} courtesy shuffles
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="game-header">
        <h2>
          Room: {gameState.roomCode}
          <span className="game-name-inline">({gameState.status})</span>
        </h2>
        <div className="game-name-editor-inline">
          <span className="game-name-label">Game Name: {gameName}</span>
        </div>
        <span className={`status-badge status--${gameState.status}`}>{gameState.status}</span>
      </div>

      {!viewerIsSeated && (
        <div className="action-bar" style={{ justifyContent: 'flex-end' }}>
          <button className="btn-cancel" onClick={() => navigate(`/game/${id}`)}>
            Back To Waiting Room
          </button>
        </div>
      )}

      {viewerIsSeated && (gameState.status === 'playing' || gameState.status === 'swap') && (
        <div className="action-bar" style={{ justifyContent: 'flex-end' }}>
          <button className="btn-cancel" onClick={handleTakeBreakToLounge}>
            Go To Waiting Room (Keep Seat)
          </button>
        </div>
      )}

      {gameState.status === 'playing' && myState?.isFinished && myFinishEntry && (
        <div className="game-flag-banner banner--out">
          🎉 I&apos;m out! Position #{myFinishEntry.position} • {myFinishEntry.points ?? 0} pts. Others continue to finish order.
          <button
            type="button"
            className="btn-cancel"
            style={{ marginLeft: '0.75rem' }}
            onClick={handleTakeBreakToLounge}
          >
            Go To Waiting Room (Keep Seat)
          </button>
        </div>
      )}

      {showRoomSettingsEntry && (
        <div className="board-settings-strip">
          <div className="board-settings-summary" aria-label="Room activity summary">
            <span className="board-settings-pill">P {seatedHumanCount}</span>
            <span className="board-settings-pill">W {waitingCount}</span>
            <span className="board-settings-pill">A {activeSeatedHumanCount}</span>
            {pendingJoinRequests.length > 0 && (
              <span className="board-settings-pill board-settings-pill--alert">
                Pending {pendingJoinRequests.length}
              </span>
            )}
          </div>
          <button
            type="button"
            className="section-toggle-btn"
            onClick={() => setRoomSettingsOpen(true)}
          >
            Room Settings
          </button>
        </div>
      )}

      {showRoomSettingsEntry && roomSettingsOpen && (
        <div
          className="room-settings-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="room-settings-modal-title"
          onClick={() => setRoomSettingsOpen(false)}
        >
          <section className="waiting-room-panel room-settings-modal" onClick={(e) => e.stopPropagation()}>
            <div className="room-settings-modal-head">
              <h3 id="room-settings-modal-title">Waiting Room Settings</h3>
              <button type="button" className="btn-cancel" onClick={() => setRoomSettingsOpen(false)}>
                Close
              </button>
            </div>

            {waitingError && <p className="error">{waitingError}</p>}

            {gameState?.roundJoinOfferWindow?.active && (
              <p className="admin-note" style={{ fontWeight: 700 }}>
                {formatJoinWindowReason(gameState.roundJoinOfferWindow.reason)}: {roundWindowSecondsRemaining}s
              </p>
            )}

            <div style={{ marginBottom: '0.85rem' }}>
              <p className="admin-note">Room code: {gameState.roomCode}</p>
              <p className="admin-note">Status: {roomStatusLabel}</p>
              {gameState.tournament?.enabled && (
                <p className="admin-note">
                  Tournament: {gameState.tournament?.label || 'Unnamed'} • {gameState.tournament?.mode === 'long-play' ? 'Long-Play' : 'Live'}
                  {gameState.tournament?.inactivityTimerEnabled ? ` • Timer ${gameState.tournament?.inactivityTimeoutMinutes || 30}m` : ' • Timer Off'}
                </p>
              )}
              <form className="game-name-editor-inline" onSubmit={handleSaveGameName}>
                <input
                  type="text"
                  maxLength={60}
                  value={gameNameInput}
                  onChange={(e) => setGameNameInput(e.target.value)}
                  placeholder="Rename room"
                />
                <button type="submit" className="btn-play">Save Name</button>
              </form>
            </div>

            {canManageNpcs && botCount > 0 && gameState.status !== 'playing' && (
              <div style={{ marginBottom: '0.8rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <button type="button" className="btn-cancel" onClick={handleEmergencyStopBotsFromGame} disabled={collapsingInactive}>
                  {collapsingInactive ? 'Stopping Bots...' : 'Emergency Stop Bots From Taking Over The World'}
                </button>
              </div>
            )}

            {(gameState.status === 'swap' || gameState.status === 'finished') && (
              <>
                <p className="admin-note">Seated player activity</p>
                <ul>
                  {(gameState.players || []).filter((p) => !p.isBot).map((p) => (
                    <li key={`seat-activity-${p.userId || p.displayName}`}>
                      <span className={`game-activity-dot ${p.isActiveInRoom ? 'active' : ''}`} style={{ marginRight: '0.45rem' }} aria-hidden="true" />
                      {p.displayName}
                      {!p.isActiveInRoom ? ' (inactive)' : ''}
                      {canBumpPlayers && p.userId !== myUserId && gameState.status !== 'playing' && (!p.isActiveInRoom || isStickler) && (
                        <button
                          type="button"
                          className="btn-cancel"
                          style={{ marginLeft: '0.6rem' }}
                          onClick={() => handleBumpFromGame(p.userId)}
                          disabled={bumpingUserId === p.userId}
                        >
                          {bumpingUserId === p.userId ? 'Removing...' : 'Toss Out'}
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              </>
            )}

            <p className="admin-note">Queued for next round</p>
            {waitingQueue.length > 0 ? (
              <ul>
                {waitingQueue.map((reqEntry, idx) => (
                  <li key={`${reqEntry.userId || reqEntry.displayName}-${idx}`}>
                    {reqEntry.displayName}
                    {reqEntry.reservationType === 'inactivity-autopilot' ? ' (Autopilot Reserved)' : ''}
                  </li>
                ))}
              </ul>
            ) : <p>No players queued yet.</p>}
          </section>
        </div>
      )}

      {(gameState.status === 'playing' || gameState.status === 'swap') && (
        <div className="game-board-stage">
          <GameTable
            gameState={gameState}
            myUserId={myUserId}
            onPlayCard={playCard}
            onPickupPile={pickupPile}
            onOpeningPass={openingPass}
            playResult={playResult}
          />

          {gameState.status === 'swap' && (
            <div className="swap-modal-overlay swap-modal-overlay--board" role="dialog" aria-modal="true" aria-labelledby="swap-modal-title">
              <div className="swap-modal-panel">
                <h3 id="swap-modal-title" className="swap-modal-title">Round Setup</h3>
                <SwapPhase
                  myState={myState}
                  onSwap={swapCards}
                  onReady={markReady}
                  readyCount={readyCount}
                  totalCount={totalCount}
                  waitingOnNames={pendingSwapPlayers}
                  onBackToWaitingRoom={handleTakeBreakToLounge}
                  onCollapseInactive={handleCollapseInactiveFromSwap}
                  onVoteCollapse={handleVoteCollapseFromSwap}
                  canManageWaitingRoom={canCollapseInactive}
                  collapsingInactive={collapsingInactive}
                  collapseProposal={gameState.collapseProposal}
                  myUserId={myUserId}
                />
                {breakError && <p className="error" style={{ marginTop: '0.5rem' }}>{breakError}</p>}
              </div>
            </div>
          )}
        </div>
      )}

      {(gameState.status === 'finished' || gameOver) && (
        <div className="game-over-overlay">
          <div className="game-over-panel">
            <h2>Round {gameOver?.roundNumber ?? gameState.seriesRound ?? 0} Complete</h2>
            <ol className="finished-order">
              {roundFinishedOrder.map((entry) => (
                <li key={entry.userId ?? entry.displayName}>
                  <span className="finish-pos">#{entry.position}</span>
                  <span className="finish-name">{entry.displayName}</span>
                  <span className="finish-pts">{entry.points ?? ''} pts</span>
                </li>
              ))}
            </ol>

            <div className="session-leaderboard">
              <h3>Session Leaderboard</h3>
              <ul>
                {sessionStats.map((entry, idx) => (
                  <li key={`${entry.userId ?? 'bot'}-${entry.displayName}`}>
                    <span className="leader-rank">#{idx + 1}</span>
                    <span className="leader-name">{entry.displayName}</span>
                    <span className="leader-metrics">
                      {entry.totalPoints ?? 0} pts • {entry.roundsPlayed ?? 0} rounds • {entry.roundsWon ?? 0} wins • {entry.courtesyShuffles ?? 0} courtesy shuffles
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rematch-panel">
              {gameState?.roundJoinOfferWindow?.active && (
                <p className="admin-note" style={{ fontWeight: 700 }}>
                  {formatJoinWindowReason(gameState.roundJoinOfferWindow.reason)}: {roundWindowSecondsRemaining}s
                </p>
              )}
              <p>{viewerIsSeated ? `Play again ready: ${rematchReadyHumans}/${rematchTotalHumans}` : 'Watching this table (not seated).'}</p>
              {viewerIsSeated && pendingRematchPlayers.length > 0 && (
                <p className="admin-note">Waiting on: {pendingRematchPlayers.join(', ')}</p>
              )}
              {breakError && <p className="error">{breakError}</p>}
              <div className="game-over-actions">
                {viewerIsSeated && (
                  <button className="btn-play" onClick={playAgainReady} disabled={myRematchReady}>
                    {myRematchReady ? 'Confirmed In For Next Round' : 'Confirm In For Next Round'}
                  </button>
                )}
                <button className="btn-cancel" onClick={handleTakeBreakToLounge}>
                  {viewerIsSeated ? 'Go To Waiting Room (Keep Seat)' : 'Back To Waiting Room'}
                </button>
                {viewerIsSeated && (
                  <button className="btn-cancel" onClick={handleQuitAfterRound} disabled={quittingRoom}>
                    {quittingRoom ? 'Leaving...' : 'Quit And Leave Room'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {showSocketModal && error && (
        <div className="socket-error-overlay" role="dialog" aria-modal="true" aria-labelledby="socket-error-title">
          <div className="socket-error-modal">
            <h3 id="socket-error-title">Connection Trouble</h3>
            <p className="socket-error-body">{error}</p>
            <p className="socket-error-help">
              If this keeps happening, check the rules page for gameplay help while connection recovers.
            </p>
            <div className="socket-error-actions">
              <button
                className="btn-cancel"
                onClick={() => {
                  clearError();
                  setShowSocketModal(false);
                }}
              >
                OK
              </button>
              <button
                className="thin-header-btn"
                onClick={() => {
                  setShowSocketModal(false);
                  navigate('/guide#how-to-play-sheet');
                }}
              >
                ? Rules
              </button>
            </div>
          </div>
        </div>
      )}

      {activeWaitlistOffer && !viewerIsSeated && (
        <div className="socket-error-overlay" role="dialog" aria-modal="true" aria-labelledby="waitlist-offer-title">
          <div className="socket-error-modal">
            <h3 id="waitlist-offer-title">Want to get in on {gameName}?</h3>
            <p className="socket-error-body">
              Players have joined {gameName}. Say yes to grab your spot before the window closes.
            </p>
            <p className="socket-error-help">
              {offerSecondsRemaining}s remaining — no reply is treated as no.
            </p>
            <div className="socket-error-actions">
              <button
                className="btn-cancel"
                onClick={() => handleWaitlistOfferResponse(false)}
                disabled={respondingToOffer}
              >
                No, skip
              </button>
              <button
                className="btn-play"
                onClick={() => handleWaitlistOfferResponse(true)}
                disabled={respondingToOffer}
              >
                {respondingToOffer ? 'Joining...' : "Yes, I'm in!"}
              </button>
            </div>
          </div>
        </div>
      )}

      {activeInactivityPrompt && (
        <div className="socket-error-overlay" role="dialog" aria-modal="true" aria-labelledby="inactivity-check-title">
          <div className="socket-error-modal">
            <h3 id="inactivity-check-title">Are You Still Playing?</h3>
            <p className="socket-error-body">
              You have been inactive. Confirm now to keep your seat, or NPC autopilot will take over.
            </p>
            <p className="socket-error-help">
              {inactivityPromptSecondsRemaining}s remaining - no reply enables autopilot reservation.
            </p>
            <div className="socket-error-actions">
              <button
                className="btn-cancel"
                onClick={() => handleInactivityPromptResponse(false)}
                disabled={respondingToInactivityPrompt}
              >
                Let NPC Take Over
              </button>
              <button
                className="btn-play"
                onClick={() => handleInactivityPromptResponse(true)}
                disabled={respondingToInactivityPrompt}
              >
                {respondingToInactivityPrompt ? 'Saving...' : "Yes, I'm Still Here"}
              </button>
            </div>
          </div>
        </div>
      )}

      <UniversalChatDock
        collapsed={chatCollapsed}
        onToggleCollapsed={() => setChatCollapsed((prev) => !prev)}
        targets={chatTargets}
        selectedTargetId={selectedChatTarget?.id || ''}
        onSelectTarget={handleSelectTarget}
        participants={selectedTargetParticipants}
        messages={visibleMessages}
        currentUserId={getUserId(user)}
        inputValue={chatText}
        onInputChange={setChatText}
        onSend={handleSendChat}
        placeholder={getPlaceholderForTarget(selectedChatTarget)}
        unreadCount={totalUnread}
        unreadByTargetId={unreadByTargetId}
        unreadNotices={unreadNotices}
        onNoticeClick={handleSelectTarget}
        onReplyToSender={(msg) => {
          if (!msg?.senderId) return;
          handleSelectTarget(`dm:${msg.senderId}`);
        }}
        canDeleteMessages={canModerateChat}
        onDeleteMessage={handleDeleteChatMessage}
        emptyState="No messages yet for this target."
        voiceControls={{
          enabled: true,
          isJoined: voiceJoined,
          isMuted: voiceMuted,
          remoteCount: remotePeers.length,
          activePipeLabel: activeVoicePipeLabel,
          selectedPipeLabel: selectedVoicePipeLabel,
          error: voiceError,
          onJoin: joinVoice,
          onJoinSelectedPipe: async () => {
            await joinVoiceTarget(selectedVoicePipeId);
          },
          onLeave: leaveVoice,
          onToggleMute: toggleVoiceMute,
          incomingCall,
          onAcceptIncomingCall: acceptIncomingCall,
          onDeclineIncomingCall: () => declineIncomingCall('declined'),
          onStartPrivateCall: async (target) => {
            const recipientId = `${target?.recipientId || ''}`;
            if (!recipientId) return;
            await callPeer(recipientId, target?.recipientName || 'Player');
          },
        }}
      />

      <div style={{ display: 'none' }}>
        {remotePeers.map((peer) => (
          <VoiceRemoteAudio key={`voice-audio-${peer.userId}`} stream={peer.stream} />
        ))}
      </div>
    </div>
  );
};

export default GamePage;
